import React, { useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import { motion } from 'framer-motion';
import { Input, Radio, RadioChangeEvent, Space, notification } from 'antd';
import { all_states } from '../abha/util';
import { IncentiveIssues, dhis_states } from '../../api/getAbhaMetricsDashboard';
import { loaderIcon } from '../common/Icons';
import { track } from '../../tracking';

const issues = [
    {
        id: 'dhis-registration-rejected',
        remark: 'My DHIS registration got rejected',
    },
    {
        id: 'pfms_error',
        remark: 'Unable to claim incentives due to account not approved by PFMS',
    },
    {
        id: 'crn_error',
        remark: 'Unable to claim incentives due to unable to get CRN number',
    },
    {
        id: 'record_linking_error',
        remark: "My Patients' records are not getting linked to their ABHA",
    },
    {
        id: 'custom_issue',
        remark: 'Other',
    },
];

const NeedHelp: React.FC<{
    close: () => void;
    onSubmit: (
        result: {
            status?: all_states;
            month?: string;
            amount?: number;
            issue?: IncentiveIssues;
            dhisstatus?: dhis_states | undefined;
            dhisissues?: IncentiveIssues | undefined;
        },
        skipMonth?: boolean,
        onSuccess?: () => void,
        onFailure?: () => void,
    ) => void;
    currMonthStatus?: {
        status?: all_states | undefined;
        month?: string | undefined;
        amount?: number | undefined;
        issue?: IncentiveIssues | undefined;
        dhisstatus?: dhis_states | undefined;
        dhisissues?: IncentiveIssues | undefined;
    };
    dhisStatus?: dhis_states;
}> = ({ close, onSubmit, currMonthStatus, dhisStatus }) => {
    const [selectedIssue, setSelectedIssue] = useState<string>();
    const [otherIssueRemark, setOtherIssueRemark] = useState<string>();
    const [loading, setLoading] = useState(false);

    const onChange = (e: RadioChangeEvent) => {
        setSelectedIssue(e.target.value);
    };

    const onIssueSubmit = () => {
        const payload =
            selectedIssue === 'custom_issue'
                ? { id: selectedIssue, remark: otherIssueRemark }
                : issues.find((issue) => issue.id === selectedIssue);

        if (!payload?.id) {
            notification.error({ message: 'Please select an issue...', duration: 3 });
            return;
        }

        if (!payload?.remark) {
            notification.error({ message: 'Please enter remark...', duration: 3 });
            return;
        }

        const finalPayload = { id: payload.id, remark: payload.remark };

        const isDhisissue =
            !dhisStatus || dhisStatus === 'dhis-registration' || dhisStatus === 'dhis-approval';

        setLoading(true);

        const month = isDhisissue ? undefined : currMonthStatus?.month;

        onSubmit(
            {
                amount: currMonthStatus?.amount,
                month,
                status: currMonthStatus?.status,
                dhisstatus: currMonthStatus?.dhisstatus,
                dhisissues: isDhisissue ? finalPayload : undefined,
                issue: isDhisissue ? undefined : finalPayload,
            },
            true,
            () => {
                close();

                track('ABHA_DASHBOARD_ISSUE_REPORTED', {
                    remark: finalPayload.remark,
                    month,
                    issue_id: finalPayload.id,
                });

                try {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    $crisp.push([
                        'do',
                        'message:send',
                        [
                            'text',
                            isDhisissue
                                ? finalPayload.remark
                                : `ABHA Issue for ${currMonthStatus?.month} => ${finalPayload.remark}`,
                        ],
                    ]);
                } catch (error) {
                    console.error(error);
                }

                setLoading(false);
                notification.success({ message: 'Issue reported successfully.', duration: 3 });
            },
            () => {
                setLoading(false);
                notification.error({ message: 'Error reporting issue!', duration: 3 });
            },
        );
    };

    return (
        <ReactHotkeys keyName="esc" onKeyDown={close}>
            <motion.div
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ zIndex: 20 }}
                transition={{ duration: 0.2, ease: 'linear' }}
                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-lato"
            >
                <div className="modal-overlay" onClick={close}></div>
                <div className="modal-content bg-darwin-neutral_new-50 w-40p h-3/5 flex flex-col">
                    <div className="flex items-center border-b border-darwin-neutral_new-200 justify-between p-16 rounded-t-4 bg-white">
                        <p className="text-20">Need help with ABHA?</p>
                        <div
                            onClick={close}
                            className="flex items-center justify-center w-16 h-16 cursor-pointer"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                className="w-16 h-16 fill-neutral-1"
                            >
                                <path d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z" />
                            </svg>
                        </div>
                    </div>
                    <div className="w-full flex-1 overflow-auto">
                        <div className="flex flex-col py-16 gap-16 px-24">
                            <div className="flex flex-col">
                                <p className="text-14 mb-16 font-700">
                                    Select the issue you want to raise
                                </p>
                                <Radio.Group onChange={onChange} value={selectedIssue}>
                                    <Space direction="vertical">
                                        {issues.map((issue) => (
                                            <Radio value={issue.id} key={issue.id}>
                                                <p className="text-13">{issue.remark}</p>
                                            </Radio>
                                        ))}
                                    </Space>
                                </Radio.Group>
                            </div>
                            {selectedIssue === 'custom_issue' && (
                                <Input.TextArea
                                    value={otherIssueRemark}
                                    placeholder="Type you remark here..."
                                    allowClear
                                    autoFocus
                                    className="rounded-4"
                                    onChange={(e) => setOtherIssueRemark(e.target.value)}
                                />
                            )}
                        </div>
                    </div>
                    <div className="border-t border-darwin-neutral_new-200 gap-16 p-12 w-full flex justify-between items-center">
                        <div
                            onClick={close}
                            className="flex font-700 bg-white cursor-pointer rounded-8 hover:bg-darwin-accent-bright-blue-100 justify-center items-center py-8 px-16 border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800"
                        >
                            Cancel
                        </div>
                        <div
                            onClick={onIssueSubmit}
                            className="flex font-700 gap-8 cursor-pointer rounded-8 hover:opacity-60 gap-4 justify-center items-center py-8 px-16 bg-darwin-accent-bright-blue-800 text-white"
                        >
                            {loading &&
                                loaderIcon(
                                    'w-20 h-20 fill-darwin-accent-bright-blue-800 font-700 animate-spin',
                                )}
                            Submit
                        </div>
                    </div>
                </div>
            </motion.div>
        </ReactHotkeys>
    );
};

export default NeedHelp;
